/**
 * Shared base filter implementations for copilot, styles, and documents filter files.
 * Each concrete filter class extends these bases and supplies its own type tag.
 */
import {
  CalendarIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import {
  type FilterDefinition,
  type FilterGeneratorContext,
  type FilterSuggestion,
  type SerializedFilter,
  type ActiveFilter
} from '@oysterjs/ui/search-filter';
import { client } from '~/api/client';
import {
  GetTeamsDocument,
  GetTeamsQuery,
  GetTeamsQueryVariables,
  GetAgencyUsersDocument,
  GetAgencyUsersQuery,
  GetAgencyUsersQueryVariables,
  Visibility
} from '~/api/graphql';

const DATE_OPTIONS = [
  'today',
  'since yesterday',
  'since last week',
  'since last month',
  'since last year'
] as const;

const DATE_MS: Record<string, number> = {
  today: 0,
  'since yesterday': 1,
  'since last week': 7,
  'since last month': 30,
  'since last year': 365
};

/** Shared by document, preset, and style date filters for "since" range matching. */
export function getSinceDate(value: string): Date | null {
  const startOfDay = new Date(new Date().setHours(0, 0, 0, 0));
  const days = DATE_MS[value];
  if (days === undefined) return null;
  return new Date(startOfDay.getTime() - days * 24 * 60 * 60 * 1000);
}

export class BaseDateFilter<T extends string> implements FilterDefinition<T> {
  readonly icon = CalendarIcon;
  readonly priority = 30;
  readonly isUnique = true;

  constructor(readonly type: T) {}

  generate({ query }: FilterGeneratorContext<T>): FilterSuggestion[] {
    return DATE_OPTIONS.filter(
      (o) => o.toLowerCase().includes(query.toLowerCase()) || 'date'.includes(query.toLowerCase())
    ).map((o) => ({
      value: o,
      displayLabel: 'Date is',
      displayValue: o
    }));
  }

  deserialize(s: SerializedFilter<T>): ActiveFilter<T> {
    return {
      id: `date-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Date is',
      displayValue: s.displayValue
    };
  }
}

export class BaseSearchFilter<T extends string> implements FilterDefinition<T> {
  readonly icon = MagnifyingGlassIcon;
  readonly priority = 100;
  readonly isUnique = false;

  constructor(readonly type: T) {}

  generate({ query }: FilterGeneratorContext<T>): FilterSuggestion[] {
    return [{ value: query, displayLabel: 'Search for', displayValue: query }];
  }

  deserialize(s: SerializedFilter<T>): ActiveFilter<T> {
    return {
      id: `search-${s.value}-${Date.now()}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Search for',
      displayValue: s.displayValue
    };
  }
}

export class BaseUserFilter<T extends string> implements FilterDefinition<T> {
  readonly icon = UserIcon;
  readonly priority = 10;
  readonly isUnique = false;
  private cachedAgencyUsers: Array<{ id: string; name: string; email: string }> | null = null;

  constructor(readonly type: T) {}

  /** Override to change which field is used as the filter value. Defaults to user id. */
  protected getUserValue(user: { id: string; name: string; email: string }): string {
    return user.id;
  }

  async generate({ query }: FilterGeneratorContext<T>): Promise<FilterSuggestion[]> {
    if (this.cachedAgencyUsers === null) {
      try {
        const data = await client.query<GetAgencyUsersQuery, GetAgencyUsersQueryVariables>({
          query: GetAgencyUsersDocument,
          variables: {}
        });
        const users = data?.data?.agencyUsers ?? [];
        this.cachedAgencyUsers = users.map((u) => ({ id: u.id, name: u.name, email: u.email }));
      } catch {
        this.cachedAgencyUsers = [];
      }
    }

    const q = query.trim().toLowerCase();
    const candidates =
      q.length === 0
        ? this.cachedAgencyUsers.slice(0, 20)
        : this.cachedAgencyUsers.filter(
            (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
          );

    return candidates.slice(0, 20).map((u) => ({
      value: this.getUserValue(u),
      displayLabel: 'User is',
      displayValue: u.name
    }));
  }

  deserialize(s: SerializedFilter<T>): ActiveFilter<T> {
    return {
      id: `user-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'User is',
      displayValue: s.displayValue
    };
  }
}

const VISIBILITY_OPTIONS: { value: Visibility; label: string }[] = [
  { value: Visibility.Private, label: 'Private' },
  { value: Visibility.Shared, label: 'Shared' },
  { value: Visibility.Public, label: 'Public' }
];

/**
 * Visibility-level filter (PRIVATE / SHARED / PUBLIC). The serialized value is
 * the Visibility enum string; entity files supply their own type tag and the
 * filter-mapping function then translates it into the backend
 * VisibilityListFilterInput.visibilityLevel.
 */
export class BaseVisibilityFilter<T extends string> implements FilterDefinition<T> {
  readonly icon = LockClosedIcon;
  readonly priority = 20;
  readonly isUnique = true;

  constructor(readonly type: T) {}

  generate({ query }: FilterGeneratorContext<T>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    return VISIBILITY_OPTIONS.filter(
      (o) => o.label.toLowerCase().includes(q) || 'visibility'.includes(q)
    ).map((o) => ({
      value: o.value,
      displayLabel: 'Visibility is',
      displayValue: o.label
    }));
  }

  deserialize(s: SerializedFilter<T>): ActiveFilter<T> {
    return {
      id: `visibility-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Visibility is',
      displayValue: s.displayValue
    };
  }
}

/**
 * "Shared with me" filter — selects the SHARED rows the caller is a target of
 * and did not author. The serialized value is unused (the filter is a toggle);
 * entity files map its presence into VisibilityListFilterInput.sharedWithMe.
 */
export class BaseSharedWithMeFilter<T extends string> implements FilterDefinition<T> {
  readonly icon = UserGroupIcon;
  readonly priority = 25;
  readonly isUnique = true;

  constructor(readonly type: T) {}

  generate({ query }: FilterGeneratorContext<T>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    if (q.length > 0 && !'shared with me'.includes(q)) {
      return [];
    }
    return [
      {
        value: 'true',
        displayLabel: 'Shared with me',
        displayValue: 'Yes'
      }
    ];
  }

  deserialize(s: SerializedFilter<T>): ActiveFilter<T> {
    return {
      id: `shared-with-me`,
      type: this.type,
      value: s.value,
      displayLabel: 'Shared with me',
      displayValue: s.displayValue
    };
  }
}

/**
 * Filter restricting list results to a single team. The serialized value is the
 * team ID; the displayed value is the team name. Suggestions come from the
 * agency's active team list (cached after the first fetch, mirroring the
 * BaseUserFilter pattern). Stale serialized IDs that no longer match a live
 * team still deserialize so saved filter sets continue to load — they just
 * render with the bare ID until the user removes them.
 */
export class BaseTeamFilter<T extends string> implements FilterDefinition<T> {
  readonly icon = UserGroupIcon;
  readonly priority = 15;
  readonly isUnique = true;
  private cachedAgencyTeams: Array<{ id: string; name: string }> | null = null;

  constructor(readonly type: T) {}

  async generate({ query }: FilterGeneratorContext<T>): Promise<FilterSuggestion[]> {
    if (this.cachedAgencyTeams === null) {
      try {
        const data = await client.query<GetTeamsQuery, GetTeamsQueryVariables>({
          query: GetTeamsDocument,
          variables: {}
        });
        const teams = data?.data?.teams ?? [];
        this.cachedAgencyTeams = teams
          .filter((t) => !t.archivedAt)
          .map((t) => ({ id: t.id, name: t.name }));
      } catch {
        this.cachedAgencyTeams = [];
      }
    }

    const q = query.trim().toLowerCase();
    const candidates =
      q.length === 0
        ? this.cachedAgencyTeams.slice(0, 20)
        : this.cachedAgencyTeams.filter((t) => t.name.toLowerCase().includes(q));

    return candidates.slice(0, 20).map((t) => ({
      value: t.id,
      displayLabel: 'Shared with team',
      displayValue: t.name
    }));
  }

  deserialize(s: SerializedFilter<T>): ActiveFilter<T> {
    return {
      id: `team-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Shared with team',
      displayValue: s.displayValue || s.value
    };
  }
}

/**
 * Minimal subset of the loaded agency user needed for the client-side
 * `sharedWithMe` / `team` filters: the viewer's user ID and team IDs. Each
 * library list (presets, styles, templates, custom forms) passes one of
 * these into its filter function alongside the active filter set.
 */
export interface FilterViewer {
  userId: string;
  teamIds: string[];
}

/**
 * Anything that carries the standard library-entity visibility shape: a
 * VisibilityConfig (level + access lists) and a non-null creator ID. The
 * library list fragments for presets, HTML templates, Word templates, and
 * custom forms all match.
 */
export interface VisibilityFilterable {
  visibility: { level: Visibility; userIds: string[]; teamIds: string[] };
  // Null/absent for a cross-agency shared preset (agency-level attribution; no author exposed).
  createdBy?: { id?: string | null; name?: string } | null;
}

/**
 * Client-side match for the three visibility filter chips (visibilityLevel /
 * sharedWithMe / team). Returns true when `filter` is one of those three and
 * `item` satisfies it; returns null when `filter` is some other type so the
 * caller can fall through to its own entity-specific matching. This is
 * always-on-readable data — every list fragment selects visibility/createdBy
 * — so no extra fetch is required.
 */
export function matchesVisibilityFilter<T extends string>(
  item: VisibilityFilterable,
  filter: ActiveFilter<T>,
  viewer: FilterViewer
): boolean | null {
  switch (filter.type) {
    case 'visibility':
      return item.visibility.level === (filter.value as Visibility);
    case 'sharedWithMe': {
      if (item.visibility.level !== Visibility.Shared) return false;
      if (item.createdBy?.id === viewer.userId) return false;
      if (item.visibility.userIds.includes(viewer.userId)) return true;
      return item.visibility.teamIds.some((t) => viewer.teamIds.includes(t));
    }
    case 'team':
      return (
        item.visibility.level === Visibility.Shared &&
        item.visibility.teamIds.includes(filter.value as string)
      );
    default:
      return null;
  }
}
